import {
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  children: ReactNode;
  text?: ReactNode;
  content?: ReactNode;
  placement?: Placement;
  wide?: boolean;
  delayMs?: number;
}

const VIEWPORT_PADDING = 8;
const TRIGGER_GAP = 6;

export function Tooltip({
  children,
  text,
  content,
  placement = 'top',
  wide = false,
  delayMs = 200,
}: TooltipProps): JSX.Element {
  const body = content ?? text;
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; actualPlacement: Placement }>({
    top: 0,
    left: 0,
    actualPlacement: placement,
  });
  const showTimer = useRef<number | null>(null);
  const tooltipId = useId();

  const compute = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;
    const tr = trigger.getBoundingClientRect();
    const tw = tooltip.offsetWidth;
    const th = tooltip.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let chosen: Placement = placement;
    if (placement === 'top' && tr.top - th - TRIGGER_GAP < VIEWPORT_PADDING) chosen = 'bottom';
    if (placement === 'bottom' && tr.bottom + th + TRIGGER_GAP > vh - VIEWPORT_PADDING) chosen = 'top';
    if (placement === 'left' && tr.left - tw - TRIGGER_GAP < VIEWPORT_PADDING) chosen = 'right';
    if (placement === 'right' && tr.right + tw + TRIGGER_GAP > vw - VIEWPORT_PADDING) chosen = 'left';

    let top = 0;
    let left = 0;
    if (chosen === 'top') {
      top = tr.top - th - TRIGGER_GAP;
      left = tr.left + tr.width / 2 - tw / 2;
    } else if (chosen === 'bottom') {
      top = tr.bottom + TRIGGER_GAP;
      left = tr.left + tr.width / 2 - tw / 2;
    } else if (chosen === 'left') {
      top = tr.top + tr.height / 2 - th / 2;
      left = tr.left - tw - TRIGGER_GAP;
    } else {
      top = tr.top + tr.height / 2 - th / 2;
      left = tr.right + TRIGGER_GAP;
    }

    left = Math.max(VIEWPORT_PADDING, Math.min(left, vw - tw - VIEWPORT_PADDING));
    top = Math.max(VIEWPORT_PADDING, Math.min(top, vh - th - VIEWPORT_PADDING));

    setCoords({ top, left, actualPlacement: chosen });
  }, [placement]);

  useLayoutEffect(() => {
    if (visible) compute();
  }, [visible, compute, body]);

  useEffect(() => {
    if (!visible) return;
    const onScrollOrResize = () => compute();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('keydown', onKey);
    };
  }, [visible, compute]);

  const open = useCallback(() => {
    if (showTimer.current !== null) window.clearTimeout(showTimer.current);
    showTimer.current = window.setTimeout(() => setVisible(true), delayMs);
  }, [delayMs]);

  const close = useCallback(() => {
    if (showTimer.current !== null) {
      window.clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    setVisible(false);
  }, []);

  useEffect(() => () => {
    if (showTimer.current !== null) window.clearTimeout(showTimer.current);
  }, []);

  const sharedEventHandlers = {
    onMouseEnter: open,
    onMouseLeave: close,
    onFocus: open,
    onBlur: close,
  };

  let trigger: ReactNode;
  if (isValidElement(children)) {
    const childEl = children as ReactElement & { ref?: React.Ref<HTMLElement> };
    trigger = cloneElement(childEl, {
      ref: (node: HTMLElement | null) => {
        triggerRef.current = node;
        const orig = childEl.ref as
          | React.MutableRefObject<HTMLElement | null>
          | ((n: HTMLElement | null) => void)
          | null;
        if (typeof orig === 'function') orig(node);
        else if (orig && 'current' in orig) (orig as React.MutableRefObject<HTMLElement | null>).current = node;
      },
      'aria-describedby': visible ? tooltipId : undefined,
      ...sharedEventHandlers,
    });
  } else {
    trigger = (
      <span
        ref={(n) => {
          triggerRef.current = n;
        }}
        aria-describedby={visible ? tooltipId : undefined}
        {...sharedEventHandlers}
        className="inline-flex"
      >
        {children}
      </span>
    );
  }

  const widthCls = wide ? 'max-w-md' : 'max-w-sm';

  const portal =
    !visible || typeof document === 'undefined' || !body
      ? null
      : createPortal(
          <div
            id={tooltipId}
            ref={tooltipRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              pointerEvents: 'none',
              zIndex: 50,
            }}
            className={`${widthCls} px-2.5 py-1.5 rounded-md border border-dash-border bg-dash-surface text-xs text-dash-primary shadow-lg whitespace-normal break-words`}
          >
            {body}
          </div>,
          document.body,
        );

  return (
    <>
      {trigger}
      {portal}
    </>
  );
}
